import { inject, Injectable } from '@angular/core';
import { BathroomConfigurationDto } from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-configuration-dtos';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { BathroomConfigurationService } from './bathroom-configuration.service';
import { Router } from '@angular/router';
import { BathroomService } from '../../applications/bathroom.service';
import { TranslateService } from '@ngx-translate/core';
import { ProcessQuoteService } from 'src/app/services/process-quote.service';

@Injectable({
  providedIn: 'root',
})
export class BathroomEndingService {
  private _configurationService = inject(BathroomConfigurationService);
  private _router = inject(Router);
  private _modalService = inject(ModalService);
  private _bathroomService = inject(BathroomService);
  private _translateService = inject(TranslateService);
  private _processQuoteService = inject(ProcessQuoteService);

  public async createConfiguration(displaySuccess: boolean = true): Promise<BathroomConfigurationDto> {
    const createdConfiguration = await this._configurationService.createConfigurationAsync();

    if (displaySuccess) {
      const compositionId = this._configurationService.getCurrentConfiguration().composition?.id;
      await this._router.navigate(['/salle-de-bain-mobile/configuration', compositionId, 'fin']);

      this.displayEndingModal({
        titleRef: 'Projet enregistré',
        contentRef: 'Votre configuration a été enregistrée avec succès.',
        primaryButtonAction: () => this._router.navigateByUrl('/'),
      });
    }

    return createdConfiguration;
  }

  public async sendToComPlus(): Promise<void> {
    const configuration = await this.createConfiguration(false);

    // On appelle cette méthode pour définir le context de la configuration et ses données dans les services qui vont être utilisés pour la suite du process
    // On simule un peu le fait d'être dans le tunnel
    await this._bathroomService.loadBathroomConfigurationFromId(configuration.id);

    const res = await this._bathroomService.generateXML(configuration.id);

    let opportunityNumber: number;
    try {
      opportunityNumber = await this.sendComPlus(res.xml);
    } catch (error) {
      logError(error);
      return;
    }

    // Lock la configuration après l'envoi à COM+
    await this._bathroomService.lockUnlockBathroomConfiguration();

    await this._bathroomService.updateBathroomConfiguration(
      {
        opportunityNumber: opportunityNumber,
      },
      false
    );

    const compositionId = this._configurationService.getCurrentConfiguration().composition?.id;
    await this._router.navigate(['/salle-de-bain-mobile/configuration', compositionId, 'fin']);

    this.displayEndingModal({
      titleRef: 'popins.opportunityCreated.title',
      contentRef: 'popins.opportunityCreated.text',
      contentArgs: { opportunityNumber },
      primaryButtonAction: () => this._router.navigateByUrl('/'),
    });
  }

  private displayEndingModal(params: {
    titleRef: string;
    contentRef: string;
    primary?: boolean;
    contentArgs?: Record<string, any> | null;
    primaryButtonAction: () => void;
  }): void {
    this._modalService.display(MODAL.MESSAGE, {
      titleRef: params.titleRef,
      contentRef: params.contentRef,
      primary: params.primary ?? true,
      ...(params.contentArgs ? { contentArgs: params.contentArgs } : {}),
      buttons: [
        {
          label: "Retour à l'accueil",
          primary: true,
          action: () => {
            this._modalService.close();
            params.primaryButtonAction();
          },
        },
        {
          label: 'Fermer',
          primary: false,
          action: () => this._modalService.close(),
        },
      ],
    });
  }

  private async sendComPlus(xml: string): Promise<number> {
    let opportunityNumber;
    try {
      opportunityNumber = await this._processQuoteService.sendDotComPlusOrder(xml);
      return opportunityNumber;
    } catch (error: any) {
      throw this._translateService.instant('validation.com_plus_error.kitchen_send_error', {
        error: JSON.stringify(error),
      });
    }
  }
}
